import AdminKeyService from './AdminKeyService';
import EncryptionKeyService from './EncryptionKeyService';

class UrlService {
    // Extract admin keys from URL and store in localStorage
    extractAndStoreAdminKeys(location, electionId, voteId) {
        const urlParams = new URLSearchParams(location.search);
        
        // Extract election admin key
        const electionAdminKey = urlParams.get('electionAdminKey');
        if (electionAdminKey && electionId) {
            // Only store if we don't already have this key
            if (!AdminKeyService.hasElectionAdminKey(electionId)) {
                AdminKeyService.setElectionAdminKey(electionId, electionAdminKey);
            }
        }
        
        // Extract vote admin key
        const voteAdminKey = urlParams.get('voteAdminKey');
        if (voteAdminKey && voteId) {
            // Only store if we don't already have this key
            if (!AdminKeyService.hasVoteAdminKey(voteId)) {
                AdminKeyService.setVoteAdminKey(voteId, voteAdminKey);
            }
        }
    }

    // Get encryption parameters from URL fragment (without storing them)
    // Returns null if parameters are incomplete or missing
    getEncryptionParamsFromUrl() {
        const hash = window.location.hash;
        if (!hash || hash.length <= 1) {
            return null;
        }

        // Remove the '#' and parse as URLSearchParams
        const hashParams = new URLSearchParams(hash.substring(1));

        // Extract encryption parameters
        const encScheme = hashParams.get('encScheme');
        const encKey = hashParams.get('encKey');

        // Only return if both parameters are present
        if (encScheme && encKey) {
            return {
                scheme: parseInt(encScheme, 10),
                key: encKey
            };
        }

        return null;
    }

    // Extract encryption keys from URL fragment and store in localStorage
    extractAndStoreEncryptionKeys(location, electionId) {
        if (!electionId) {
            return;
        }

        // Parse the hash fragment
        const hash = location.hash;
        if (!hash || hash.length <= 1) {
            // No hash params, but add them if we have stored keys
            this.addEncryptionKeysToUrl(electionId);
            return;
        }

        // Remove the '#' and parse as URLSearchParams
        const hashParams = new URLSearchParams(hash.substring(1));

        // Extract encryption parameters
        const encScheme = hashParams.get('encScheme');
        const encKey = hashParams.get('encKey');

        // Only store if both parameters are present and we don't already have keys for this election
        // If we have keys, try them first - ApiService.handleDecryptionFailure() will handle URL fallback
        if (encScheme && encKey) {
            if (!EncryptionKeyService.hasEncryptionKey(electionId)) {
                const encryptionParams = {
                    scheme: parseInt(encScheme, 10),
                    key: encKey
                };
                EncryptionKeyService.setEncryptionKey(electionId, encryptionParams);
            }
            // URL already has params, no need to update
        } else {
            // Incomplete or no params in URL, add them if we have them stored
            this.addEncryptionKeysToUrl(electionId);
        }
    }

    // Add encryption keys to current URL fragment (updates browser address bar)
    addEncryptionKeysToUrl(electionId) {
        if (!electionId) {
            return;
        }

        const encryptionParams = EncryptionKeyService.getEncryptionKey(electionId);
        if (!encryptionParams) {
            return; // No encryption params to add
        }

        const fragment = new URLSearchParams({
            encScheme: encryptionParams.scheme.toString(),
            encKey: encryptionParams.key
        });

        const url = new URL(window.location);
        url.hash = fragment.toString();
        window.history.replaceState({}, '', url);
    }

    // Synchronize URL fragment with stored encryption keys
    // Call after successful decryption to ensure URL shows correct encryption params
    syncEncryptionKeysWithUrl(electionId) {
        if (!electionId) {
            return;
        }

        const storedParams = EncryptionKeyService.getEncryptionKey(electionId);
        if (!storedParams) {
            return; // No params to sync
        }

        const urlParams = this.getEncryptionParamsFromUrl();

        // If URL params are missing or differ from localStorage, update them
        if (!urlParams ||
            urlParams.scheme !== storedParams.scheme ||
            urlParams.key !== storedParams.key) {
            this.addEncryptionKeysToUrl(electionId);
        }
    }

    // Build URL with encryption parameters appended
    buildEncryptedElectionUrl(path, electionId) {
        if (!electionId) {
            return path;
        }

        const encryptionParams = EncryptionKeyService.getEncryptionKey(electionId);
        if (!encryptionParams) {
            return path; // No encryption params, return unchanged
        }

        const fragment = new URLSearchParams({
            encScheme: encryptionParams.scheme.toString(),
            encKey: encryptionParams.key
        });

        return `${path}#${fragment.toString()}`;
    }

    // Generate share URLs
    generateElectionShareUrl(electionId, includeAdminKey = false, isEncrypted = false) {
        let url = `${window.location.origin}/ranked-elections/${electionId}`;

        // Add admin key as query parameter if requested
        if (includeAdminKey) {
            const adminKey = AdminKeyService.getElectionAdminKey(electionId);
            if (adminKey) {
                url += `?electionAdminKey=${adminKey}`;
            }
        }

        // Only add encryption parameters if election is encrypted
        if (isEncrypted) {
            const encryptionParams = EncryptionKeyService.getEncryptionKey(electionId);
            if (encryptionParams) {
                const fragment = new URLSearchParams({
                    encScheme: encryptionParams.scheme.toString(),
                    encKey: encryptionParams.key
                });
                url += `#${fragment.toString()}`;
            }
        }

        return url;
    }

    generateVoteShareUrl(electionId, voteId, includeAdminKey = false, isEncrypted = false) {
        let url = `${window.location.origin}/ranked-elections/${electionId}/votes/${voteId}`;

        // Add admin key as query parameter if requested
        if (includeAdminKey) {
            const adminKey = AdminKeyService.getVoteAdminKey(voteId);
            if (adminKey) {
                url += `?voteAdminKey=${adminKey}`;
            }
        }

        // Only add encryption parameters if election is encrypted
        if (isEncrypted) {
            const encryptionParams = EncryptionKeyService.getEncryptionKey(electionId);
            if (encryptionParams) {
                const fragment = new URLSearchParams({
                    encScheme: encryptionParams.scheme.toString(),
                    encKey: encryptionParams.key
                });
                url += `#${fragment.toString()}`;
            }
        }

        return url;
    }

    // Remove admin keys from current URL
    removeAdminKeysFromUrl() {
        const url = new URL(window.location);
        url.searchParams.delete('electionAdminKey');
        url.searchParams.delete('voteAdminKey');
        window.history.replaceState({}, '', url);
    }

    // Remove encryption keys from current URL fragment
    removeEncryptionKeysFromUrl() {
        const url = new URL(window.location);
        // Simply remove the hash
        url.hash = '';
        window.history.replaceState({}, '', url);
    }
}

const urlService = new UrlService();
export default urlService;